import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  TextField,
  IconButton,
  Box,
  Chip,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
  Collapse,
  keyframes,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import AddCircle from "@mui/icons-material/AddCircle";
import SearchIcon from "@mui/icons-material/Search";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import ShareIcon from "@mui/icons-material/Share";
import HistoryIcon from "@mui/icons-material/History";
import CloseIcon from "@mui/icons-material/Close";
import SortIcon from "@mui/icons-material/Sort";
import supabase from "../supabaseClient";
import UserContext from "../contexts/userContext";

// Animations
const checkBounce = keyframes`
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.3); }
  100% { transform: scale(1); opacity: 1; }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Recent lists storage key
const RECENT_LISTS_KEY = 'recipe-catalog-recent-lists';
const MAX_RECENT_LISTS = 3;

function RecipeAddToListDialog({ open, onClose, selectedRecipeId }) {
  const {user} = useContext(UserContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [selectedListIds, setSelectedListIds] = useState([]);
  const [newListName, setNewListName] = useState("");
  const [recipeInLists, setRecipeInLists] = useState([]);
  const [showCreateList, setShowCreateList] = useState(false);
  const [userLists, setUserLists] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [listCounts, setListCounts] = useState({});
  
  // Animation and feedback states
  const [animatingListId, setAnimatingListId] = useState(null);
  const [newlyCreatedListId, setNewlyCreatedListId] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [lastAddedListIds, setLastAddedListIds] = useState([]);
  const [recentListIds, setRecentListIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(RECENT_LISTS_KEY)) || [];
    } catch {
      return [];
    }
  });

  // Save recent lists to localStorage
  const updateRecentLists = useCallback((listIds) => {
    const updated = [...new Set([...listIds, ...recentListIds])].slice(0, MAX_RECENT_LISTS);
    setRecentListIds(updated);
    localStorage.setItem(RECENT_LISTS_KEY, JSON.stringify(updated));
  }, [recentListIds]);

  useEffect(() => {
    if (user) {
      const fetchUserLists = async () => {
        // Fetch owned lists
        const { data: ownedLists, error: ownedError } = await supabase
          .from("saved_lists")
          .select("*")
          .eq("user_id", user.id);

        if (ownedError) {
          console.error("Error fetching owned lists:", ownedError);
          return;
        }

        // Fetch shared lists (lists shared with household where user is a member)
        // First, get user's household
        const { data: householdUser } = await supabase
          .from("households_users")
          .select("household_id")
          .eq("user_id", user.id)
          .single();

        let sharedLists = [];
        if (householdUser) {
          // Get all users in the same household
          const { data: householdMembers } = await supabase
            .from("households_users")
            .select("user_id")
            .eq("household_id", householdUser.household_id);

          if (householdMembers) {
            const householdUserIds = householdMembers.map(m => m.user_id);
            // Get lists owned by household members that are shared
            const { data: shared } = await supabase
              .from("saved_lists")
              .select("*")
              .in("user_id", householdUserIds)
              .eq("shared_with_household", true)
              .neq("user_id", user.id); // Exclude own lists (already in ownedLists)

            sharedLists = shared || [];
          }
        }

        // Combine owned and shared lists
        const allLists = [...(ownedLists || []), ...sharedLists];
        setUserLists(allLists);

        // Fetch recipe counts for each list
        const counts = {};
        for (const list of allLists) {
          const { count } = await supabase
            .from("lists_recipes")
            .select("*", { count: "exact", head: true })
            .eq("list_id", list.list_id);
          counts[list.list_id] = count || 0;
        }
        setListCounts(counts);
      };

      fetchUserLists();
    }
  }, [user]);

  useEffect(() => {
    const fetchListsForRecipe = async () => {
      if (selectedRecipeId) {
        const { data, error } = await supabase
          .from("lists_recipes")
          .select("list_id")
          .eq("recipe_id", selectedRecipeId);

        if (!error) {
          setRecipeInLists(data.map((item) => item.list_id));
        }
      }
    };

    if (open) {
      fetchListsForRecipe();
    }
  }, [open, selectedRecipeId]);

  const handleCreateList = async () => {
    if (newListName.trim() === "") return;

    const { data, error } = await supabase
      .from("saved_lists")
      .insert([{ list_name: newListName, user_id: user.id }])
      .single()
      .select();

    if (error) {
      console.error("Error creating new list:", error);
    } else {
      setNewListName("");
      setShowCreateList(false);
      setNewlyCreatedListId(data.list_id);
      setUserLists((prev) => [data, ...prev]); // Add to beginning for visibility
      // Auto-select the new list
      setSelectedListIds((prev) => [...prev, data.list_id]);
      setAnimatingListId(data.list_id);
      setTimeout(() => {
        setAnimatingListId(null);
        setNewlyCreatedListId(null);
      }, 500);
    }
  };

  const handleAddToList = async (e) => {
    e.stopPropagation();

    if (selectedListIds.length === 0 || !selectedRecipeId) return;

    const insertData = selectedListIds.map((listId) => ({
      list_id: listId,
      recipe_id: selectedRecipeId,
    }));

    const addedListIds = [...selectedListIds];
    const listNames = addedListIds
      .map(id => userLists.find(l => l.list_id === id)?.list_name)
      .filter(Boolean);

    try {
      const { error } = await supabase.from("lists_recipes").insert(insertData);

      if (error) {
        throw error;
      }

      // Update recent lists
      updateRecentLists(addedListIds);
      
      // Store for potential undo
      setLastAddedListIds(addedListIds);
      
      // Show success message
      const message = listNames.length === 1 
        ? `Added to "${listNames[0]}"` 
        : `Added to ${listNames.length} lists`;
      setSnackbarMessage(message);
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error adding recipe to lists:", error);
    }

    // Reset states and close the dialog
    setSelectedListIds([]);
    setShowCreateList(false);
    onClose();
  };

  // Undo add to list
  const handleUndoAdd = async () => {
    if (lastAddedListIds.length === 0 || !selectedRecipeId) return;

    try {
      for (const listId of lastAddedListIds) {
        await supabase
          .from("lists_recipes")
          .delete()
          .eq("list_id", listId)
          .eq("recipe_id", selectedRecipeId);
      }
      setSnackbarOpen(false);
      setLastAddedListIds([]);
    } catch (error) {
      console.error("Error undoing add:", error);
    }
  };

  const handleRemoveFromList = async (e, listId) => {
    e.stopPropagation();

    try {
      const { data, error } = await supabase
        .from("lists_recipes")
        .delete()
        .eq("list_id", listId)
        .eq("recipe_id", selectedRecipeId);

      if (error) {
        throw error;
      }

      console.log("Recipe removed successfully:", data);

      // Update recipeInLists to remove the listId
      setRecipeInLists((prev) => prev.filter((id) => id !== listId));
    } catch (error) {
      console.error("Error removing recipe from list:", error);
    }
  };

  // Filter and sort lists
  const filteredAndSortedLists = useMemo(() => {
    let filtered = userLists.filter(list => 
      list.list_name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Sort lists
    switch (sortBy) {
      case "alphabetical":
        filtered.sort((a, b) => a.list_name.localeCompare(b.list_name));
        break;
      case "recipeCount":
        filtered.sort((a, b) => (listCounts[b.list_id] || 0) - (listCounts[a.list_id] || 0));
        break;
      case "recent":
        filtered.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        break;
      case "alreadyIn":
        filtered.sort((a, b) => {
          const aInList = recipeInLists.includes(a.list_id);
          const bInList = recipeInLists.includes(b.list_id);
          if (aInList && !bInList) return -1;
          if (!aInList && bInList) return 1;
          return a.list_name.localeCompare(b.list_name);
        });
        break;
      default:
        // Default to alphabetical sorting
        filtered.sort((a, b) => a.list_name.localeCompare(b.list_name));
        break;
    }

    return filtered;
  }, [userLists, searchQuery, sortBy, listCounts, recipeInLists]);

  // Handle list selection with animation
  const handleListSelect = (listId, isAlreadyInList) => {
    if (isAlreadyInList) return;
    
    const isSelected = selectedListIds.includes(listId);
    
    if (isSelected) {
      setSelectedListIds((prev) => prev.filter((id) => id !== listId));
    } else {
      setSelectedListIds((prev) => [...prev, listId]);
      setAnimatingListId(listId);
      setTimeout(() => setAnimatingListId(null), 300);
    }
  };

  const handleClose = () => {
    setSelectedListIds([]);
    setShowCreateList(false);
    setSearchQuery("");
    onClose();
  };

  // Get recent lists that exist in userLists
  const recentLists = useMemo(() => {
    return recentListIds
      .map(id => userLists.find(l => l.list_id === id))
      .filter(Boolean)
      .slice(0, MAX_RECENT_LISTS);
  }, [recentListIds, userLists]);

  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.accent;

  // Sort options with labels
  const sortOptions = {
    alphabetical: 'a-z',
    recipeCount: 'most recipes',
    recent: 'recent',
    alreadyIn: 'in list first',
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      fullWidth 
      maxWidth={isMobile ? "sm" : "md"}
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          ...(isMobile && {
            // Match the desktop glassmorphism style
            background: isDark
              ? 'linear-gradient(135deg, rgba(30, 30, 35, 0.98) 0%, rgba(20, 20, 25, 0.99) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
            display: 'flex',
            flexDirection: 'column',
          }),
        },
      }}
    >
      <DialogTitle sx={{ 
        pb: 1.5,
        ...(isMobile && {
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: isDark ? 'rgba(28, 28, 32, 0.95)' : 'rgba(252, 252, 254, 0.95)',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
        }),
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            {isMobile && (
              <IconButton 
                onClick={handleClose}
                size="small"
                sx={{ 
                  ml: -1,
                  color: 'text.secondary',
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
            <Typography 
              sx={{ 
                fontSize: isMobile ? '0.95rem' : '1rem',
                fontWeight: 600,
                letterSpacing: '-0.01em',
              }}
            >
              add recipe to list
            </Typography>
          </Box>
          {selectedListIds.length > 0 && (
            <Chip 
              label={`${selectedListIds.length} selected`} 
              size="small"
              sx={{
                backgroundColor: `${accent.primary}20`,
                color: accent.primary,
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 24,
                border: `1px solid ${accent.primary}40`,
              }}
            />
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ 
        ...(isMobile && { 
          flex: 1, 
          overflowY: 'auto',
          px: 2,
          pt: 2,
        }),
      }}>
        {/* Search and Sort Controls */}
        <Box sx={{ mb: 2.5, display: "flex", gap: 1 }}>
          <TextField
            fullWidth
            placeholder="search lists..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                height: 40,
              },
              '& .MuiInputBase-input': {
                fontSize: '0.85rem',
              },
            }}
          />
          <FormControl size="small" sx={{ minWidth: 130, flexShrink: 0 }}>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              displayEmpty
              renderValue={(value) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <SortIcon sx={{ fontSize: 16, opacity: 0.6 }} />
                  <Typography sx={{ fontSize: '0.8rem' }}>
                    {sortOptions[value]}
                  </Typography>
                </Box>
              )}
              sx={{
                backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                height: 40,
                '& .MuiSelect-select': {
                  fontSize: '0.8rem',
                  py: 1,
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 0.5,
                    background: isDark
                      ? 'linear-gradient(135deg, rgba(30, 30, 35, 0.98) 0%, rgba(20, 20, 25, 0.99) 100%)'
                      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                  },
                },
              }}
            >
              <MenuItem value="recent" sx={{ fontSize: '0.85rem' }}>recent</MenuItem>
              <MenuItem value="alphabetical" sx={{ fontSize: '0.85rem' }}>a-z</MenuItem>
              <MenuItem value="recipeCount" sx={{ fontSize: '0.85rem' }}>most recipes</MenuItem>
              <MenuItem value="alreadyIn" sx={{ fontSize: '0.85rem' }}>in list first</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Create New List - Always visible at top */}
        <Collapse in={showCreateList}>
          <Box 
            sx={{ 
              mb: 2,
              p: 2, 
              border: `1.5px dashed ${accent.primary}50`,
              borderRadius: '12px',
              backgroundColor: `${accent.primary}08`,
            }}
          >
            <Box display="flex" gap={1.5} alignItems="center">
              <TextField
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                placeholder="list name"
                variant="outlined"
                fullWidth
                size="small"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateList();
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.8)',
                    height: isMobile ? 44 : 40,
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.9rem',
                  },
                }}
              />
              <IconButton
                onClick={handleCreateList}
                disabled={!newListName.trim()}
                sx={{ 
                  width: isMobile ? 44 : 38,
                  height: isMobile ? 44 : 38,
                  bgcolor: accent.primary,
                  color: isDark ? '#121212' : '#fff',
                  borderRadius: '10px',
                  flexShrink: 0,
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  "&:hover": {
                    bgcolor: accent.primary,
                    transform: 'scale(1.05)',
                    boxShadow: `0 4px 14px -2px ${accent.primary}60`,
                  },
                  "&:active": {
                    transform: 'scale(0.95)',
                  },
                  "&.Mui-disabled": {
                    bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                    color: 'text.disabled',
                  }
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Collapse>
        
        {!showCreateList && (
          <Box 
            onClick={() => setShowCreateList(true)}
            sx={{ 
              mb: 2,
              p: isMobile ? 1.5 : 1.25, 
              border: `1.5px dashed ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'}`,
              borderRadius: '10px',
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              "&:hover": {
                backgroundColor: `${accent.primary}08`,
                borderColor: `${accent.primary}50`,
              },
              "&:active": {
                transform: 'scale(0.98)',
              }
            }}
          >
            <AddCircle sx={{ fontSize: 16, color: accent.primary }} />
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: accent.primary }}>
              new list
            </Typography>
          </Box>
        )}

        {/* Recent Lists Section */}
        {recentLists.length > 0 && !searchQuery && (
          <Box sx={{ mb: 2.5 }}>
            <Box display="flex" alignItems="center" gap={0.5} mb={1}>
              <HistoryIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              <Typography 
                sx={{ 
                  fontSize: '0.7rem', 
                  color: 'text.disabled',
                  fontWeight: 500,
                  textTransform: 'lowercase',
                  letterSpacing: '0.02em',
                }}
              >
                recent
              </Typography>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              {recentLists.map((list) => {
                const isAlreadyInList = recipeInLists.includes(list.list_id);
                const isSelected = selectedListIds.includes(list.list_id);
                
                return (
                  <Chip
                    key={`recent-${list.list_id}`}
                    label={list.list_name}
                    onClick={() => handleListSelect(list.list_id, isAlreadyInList)}
                    disabled={isAlreadyInList}
                    icon={isSelected ? <CheckIcon sx={{ fontSize: '14px !important' }} /> : undefined}
                    sx={{
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      height: 32,
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      backgroundColor: isAlreadyInList 
                        ? `${theme.palette.success.main}15`
                        : isSelected 
                          ? `${accent.primary}20` 
                          : isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                      border: isAlreadyInList
                        ? `1px solid ${theme.palette.success.main}40`
                        : isSelected 
                          ? `1px solid ${accent.primary}60`
                          : `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                      color: isAlreadyInList 
                        ? theme.palette.success.main
                        : isSelected 
                          ? accent.primary 
                          : 'text.primary',
                      '&:hover': {
                        backgroundColor: isSelected 
                          ? `${accent.primary}30` 
                          : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                        transform: 'translateY(-1px)',
                      },
                      ...(animatingListId === list.list_id && {
                        animation: `${checkBounce} 0.3s ease-out`,
                      }),
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        )}

        {/* Lists */}
        <List sx={{ 
          paddingTop: 0, 
          ...(!isMobile && { maxHeight: '340px', overflowY: 'auto' }),
        }}>
          {filteredAndSortedLists.map((list, index) => {
            const isAlreadyInList = recipeInLists.includes(list.list_id);
            const isSelected = selectedListIds.includes(list.list_id);
            const recipeCount = listCounts[list.list_id] || 0;
            const isNewlyCreated = newlyCreatedListId === list.list_id;

            return (
              <Card 
                key={list.list_id}
                elevation={0}
                sx={{ 
                  mb: 1,
                  cursor: isAlreadyInList ? "default" : "pointer",
                  opacity: isAlreadyInList ? 0.7 : 1,
                  borderRadius: '12px',
                  border: isSelected 
                    ? `1.5px solid ${accent.primary}` 
                    : isAlreadyInList 
                      ? `1px solid ${theme.palette.success.main}40`
                      : `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                  backgroundColor: isSelected 
                    ? `${accent.primary}12` 
                    : isAlreadyInList 
                      ? `${theme.palette.success.main}08`
                      : isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: 'translateY(0)',
                  // Staggered entrance animation
                  animation: isNewlyCreated 
                    ? `${slideIn} 0.3s ease-out`
                    : `${slideIn} 0.25s ease-out`,
                  animationDelay: isNewlyCreated ? '0ms' : `${index * 30}ms`,
                  animationFillMode: 'backwards',
                  "&:hover": {
                    backgroundColor: isAlreadyInList 
                      ? `${theme.palette.success.main}10`
                      : isSelected 
                        ? `${accent.primary}18`
                        : isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                    borderColor: isAlreadyInList 
                      ? `${theme.palette.success.main}60`
                      : isSelected 
                        ? accent.primary
                        : isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
                    transform: isAlreadyInList ? 'translateY(0)' : 'translateY(-1px)',
                    boxShadow: isAlreadyInList ? 'none' : `0 4px 12px -4px ${isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'}`,
                  }
                }}
                onClick={() => handleListSelect(list.list_id, isAlreadyInList)}
              >
                <CardContent sx={{ 
                  py: isMobile ? 1.75 : 1.5, 
                  px: 2, 
                  "&:last-child": { pb: isMobile ? 1.75 : 1.5 } 
                }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" flex={1} minWidth={0}>
                      <Box 
                        sx={{ 
                          mr: 1.5, 
                          width: 6, 
                          height: 6,
                          borderRadius: '50%',
                          transition: 'all 0.2s ease',
                          bgcolor: isAlreadyInList 
                            ? theme.palette.success.main 
                            : isSelected 
                              ? accent.primary 
                              : isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                          transform: isSelected ? 'scale(1.3)' : 'scale(1)',
                          boxShadow: isSelected ? `0 0 8px ${accent.primary}60` : 'none',
                        }}
                      />
                      <Box flex={1} minWidth={0}>
                        <Typography 
                          sx={{ 
                            fontSize: '0.9rem',
                            fontWeight: isSelected ? 600 : 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            transition: 'all 0.2s ease',
                            color: isSelected ? accent.primary : 'text.primary',
                          }}
                        >
                          {list.list_name}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1.5} mt={0.25}>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <RestaurantIcon sx={{ fontSize: 12, opacity: 0.5 }} />
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
                              {recipeCount}
                            </Typography>
                          </Box>
                          {list.user_id !== user.id && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <ShareIcon sx={{ fontSize: 12, opacity: 0.5 }} />
                              <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
                                shared
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    
                    <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
                      {isSelected && !isAlreadyInList && (
                        <CheckIcon 
                          sx={{
                            fontSize: 18,
                            color: accent.primary,
                            animation: animatingListId === list.list_id 
                              ? `${checkBounce} 0.3s ease-out` 
                              : 'none',
                          }}
                        />
                      )}
                      {isAlreadyInList && (
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFromList(e, list.list_id);
                          }}
                          sx={{ 
                            minWidth: 'auto',
                            px: 1.5,
                            py: 0.5,
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            borderRadius: '8px',
                            color: theme.palette.error.main,
                            backgroundColor: `${theme.palette.error.main}10`,
                            border: `1px solid ${theme.palette.error.main}30`,
                            transition: 'all 0.2s ease',
                            "&:hover": {
                              backgroundColor: `${theme.palette.error.main}20`,
                              borderColor: theme.palette.error.main,
                            }
                          }}
                        >
                          remove
                        </Button>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </List>
      </DialogContent>

      <DialogActions sx={{
        ...(isMobile && {
          position: 'sticky',
          bottom: 0,
          backgroundColor: isDark ? 'rgba(28, 28, 32, 0.95)' : 'rgba(252, 252, 254, 0.95)',
          backdropFilter: 'blur(12px)',
          borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
          p: 2,
          gap: 1.5,
        }),
      }}>
        {!isMobile && (
          <Button 
            onClick={handleClose}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
              },
            }}
          >
            cancel
          </Button>
        )}
        <Button 
          onClick={handleAddToList} 
          variant="contained"
          disabled={selectedListIds.length === 0}
          fullWidth={isMobile}
          sx={{
            backgroundColor: accent.primary,
            color: isDark ? '#121212' : '#fff',
            fontWeight: 600,
            ...(isMobile && {
              py: 1.25,
              fontSize: '0.9rem',
            }),
            '&:hover': {
              backgroundColor: accent.primary,
              transform: 'translateY(-1px)',
            },
            '&:disabled': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
              color: 'text.disabled',
            },
          }}
        >
          {selectedListIds.length === 0 
            ? 'select a list' 
            : `add to ${selectedListIds.length} list${selectedListIds.length !== 1 ? 's' : ''}`
          }
        </Button>
      </DialogActions>

      {/* Success Snackbar with Undo */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity="success"
          variant="filled"
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={handleUndoAdd}
              sx={{ 
                fontWeight: 600, 
                fontSize: '0.75rem',
                letterSpacing: '0.02em',
              }}
            >
              undo
            </Button>
          }
          sx={{ 
            width: '100%',
            alignItems: 'center',
            borderRadius: '12px',
            fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
            fontSize: '0.85rem',
            backdropFilter: 'blur(12px)',
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

export default RecipeAddToListDialog